import { useEffect, useRef, useState, type ReactNode } from 'react';
import { FLOW_NODE_TYPES } from '../types';
import type { BotAction, CatalogProduct, FlowDefinition, FlowNode, FlowNodeType } from '../types';
import { NODE_EXPLAIN, NODE_LABEL, NODE_ORDER, NODE_TONE, retype } from './nodeKinds';
import { TryAction, type TestAction } from './TryAction';
import { actionConfig, variableNameFor } from './placeholders';

const FIELD = 'mt-1 w-full rounded-lg border border-(--color-line) bg-(--color-paper) px-3 py-2 text-body focus-visible:outline-none focus-visible:border-(--color-brand) disabled:opacity-60';
const SMALL_FIELD = 'w-full rounded-lg border border-(--color-line) bg-(--color-paper) px-3 py-2 text-body focus-visible:outline-none focus-visible:border-(--color-brand) disabled:opacity-60';
const GHOST = 'rounded-lg border border-(--color-line) px-3 py-1.5 text-small text-(--color-muted) transition hover:text-(--color-ink) disabled:opacity-40';

/**
 * WhatsApp's limits, so the builder says "cut at 20" while somebody is typing rather
 * than the customer finding out.
 */
const LIMITS = { body: 1024, header: 60, footer: 60, button: 20, rowTitle: 24, buttonTitle: 20, rowDescription: 72, section: 24 };

/** Sentinel option in every "goes to" that creates the step it points at. */
const NEW_STEP = '__new__';

/**
 * A step, as a form that knows every field the interpreter reads.
 *
 * The first version of this exposed about a third of what a step can carry, and
 * everything else — a menu's button label, a header, the message for a wrong answer,
 * a request body — was "edited in the JSON view", which is a builder admitting it is
 * not one. Worse, it offered a Method dropdown that the validator refuses, so the
 * form produced steps that could not be published. Every field here is one the
 * runner reads; nothing here is one it does not.
 *
 * Every "goes to" is a dropdown of steps that exist, plus "a new step" that creates
 * one and links it in one move — which is how a flow is actually built: from the
 * welcome outward, one branch at a time, rather than by naming twenty steps first and
 * wiring them afterwards.
 */
export function NodeEditor({ id, node, definition, actions, readOnly = false, onChange, onRename, onRemove, onCreateStep, onTest, onProducts, names }: {
  id: string;
  node: FlowNode;
  definition: FlowDefinition;
  actions: BotAction[];
  readOnly?: boolean;
  onChange: (patch: FlowNode) => void;
  onRename: (next: string) => void;
  onRemove: () => void;
  /**
   * Creates a step and, in the same write, applies `link(newId)` to this one. The two
   * have to land together: written separately, the second would be built on a
   * definition that does not yet contain the first.
   */
  onCreateStep?: (link: (newId: string) => FlowNode) => void;
  /** Calls an action once so the answer can be picked from. Absent, paths are typed. */
  onTest?: TestAction;
  /** The catalogue, for a "show products" step. Absent, product ids are typed. */
  onProducts?: () => Promise<CatalogProduct[]>;
  /** Suggestions for the steps that name a template, a tag or another bot. */
  names?: { templates?: string[]; tags?: string[]; flows?: string[] };
}) {
  const ids = Object.keys(definition.nodes ?? {});
  // One item of the list picked from a real answer, so the id/title fields of a
  // system menu can be chosen rather than guessed.
  const [sampleItem, setSampleItem] = useState<Record<string, unknown> | null>(null);
  const type: FlowNodeType = FLOW_NODE_TYPES.includes(node.type as FlowNodeType) ? (node.type as FlowNodeType) : 'message';
  const isStart = definition.start === id;

  const set = (key: string, value: unknown) => onChange({ ...node, [key]: value });
  const setMany = (patch: Record<string, unknown>) => onChange({ ...node, ...patch });

  /** A "goes to" for one field of this step. A helper, not a nested component: a
   *  component defined inside a render is a new type every render, and React
   *  remounts it — which drops focus and, on a phone, closes the picker mid-tap. */
  const target = (label: string, field: string, none = 'Stop here') => (
    <Field label={label}>
      <TargetSelect
        value={(node[field] as string) ?? ''}
        ids={ids}
        self={id}
        none={none}
        onChange={(next) => set(field, next)}
        onCreate={onCreateStep ? (newId) => ({ ...node, [field]: newId }) : undefined}
        create={onCreateStep}
      />
    </Field>
  );

  const validation = (node.validation as Record<string, unknown> | undefined) ?? {};
  const validationType = (validation.type as string) ?? 'nonempty';
  const setValidation = (patch: Record<string, unknown>) => set('validation', { ...validation, ...patch });

  const chosenAction = actions.find((a) => a.actionKey === node.action);
  const actionCall = chosenAction ? describeAction(chosenAction) : null;
  const actionMethod = chosenAction ? (actionConfig(chosenAction).method ?? 'POST').toUpperCase() : 'POST';

  // What is already remembered, keyed by path, so the answer tree can show it.
  const pickedPaths: Record<string, string> = {};
  for (const [name, path] of Object.entries((node.response_map as Record<string, unknown>) ?? {})) pickedPaths[String(path)] = name;
  if (typeof node.items_path === 'string' && node.items_path) {
    pickedPaths[node.items_path] = 'the list';
    for (const [name, field] of Object.entries((node.field_map as Record<string, unknown>) ?? {})) pickedPaths[`${node.items_path}.0.${String(field)}`] = name;
  }

  return (
    <fieldset disabled={readOnly} className="@container min-w-0 rounded-xl border border-(--color-line) bg-(--color-surface) p-4 @md:p-5">
      {/* ------------------------------------------------------------ head -- */}
      <div className="flex items-start gap-3">
        <span className="mt-3.5 size-2.5 shrink-0 rounded-full" style={{ background: NODE_TONE[type] }} aria-hidden />
        <NameField id={id} taken={ids} onRename={onRename} />
        <label className="block w-40 shrink-0 @lg:w-48">
          <span className="sr-only">Step type</span>
          <select value={type} onChange={(e) => onChange(retype(node, e.target.value as FlowNodeType))} className={`${FIELD} mt-0`}>
            {NODE_ORDER.map((t) => <option key={t} value={t}>{NODE_LABEL[t]}</option>)}
          </select>
        </label>
      </div>
      <div className="mt-2 flex items-center gap-3">
        <p className="min-w-0 flex-1 text-small text-(--color-muted)">{NODE_EXPLAIN[type]}</p>
        {isStart && (
          <span className="shrink-0 rounded-full bg-(--color-brand-wash) px-2.5 py-1 text-small font-medium text-(--color-brand)">Starts here</span>
        )}
        <button type="button" onClick={onRemove} className={`${GHOST} shrink-0 hover:text-(--color-bad)`}>Remove</button>
      </div>

      {/* ------------------------------------------------------------ body -- */}
      <div className="mt-4 grid gap-4 @md:grid-cols-2">
        {(type === 'message' || type === 'list' || type === 'buttons' || type === 'products' || type === 'handoff' || type === 'end' || type === 'dynamic_list') && (
          <Field
            label={type === 'handoff' ? 'What it says as it hands over (optional)' : type === 'end' ? 'What it says as it ends (optional)' : 'What it says'}
            wide
            count={[(node.body as string) ?? '', type === 'message' || type === 'end' || type === 'handoff' ? 4096 : LIMITS.body]}
          >
            <textarea
              value={(node.body as string) ?? ''}
              onChange={(e) => set('body', e.target.value)}
              rows={rows(node.body)}
              className={FIELD}
              placeholder={type === 'dynamic_list' ? 'Here is what we found for you:' : 'Hello {{company}} 👋'}
            />
          </Field>
        )}

        {type === 'capture' && (
          <>
            <Field label="The question" wide count={[(node.prompt as string) ?? '', 4096]}>
              <textarea value={(node.prompt as string) ?? ''} onChange={(e) => set('prompt', e.target.value)} rows={rows(node.prompt)} className={FIELD} placeholder="What is your mobile number?" />
            </Field>
            <Field label="Remember the answer as" hint="Use it later as {{name}}.">
              <input value={(node.var as string) ?? ''} onChange={(e) => set('var', e.target.value)} className={`${FIELD} font-mono`} placeholder="mobile" />
            </Field>
            <Field label="The answer must be">
              <select value={validationType} onChange={(e) => setValidation({ type: e.target.value })} className={FIELD}>
                <option value="nonempty">anything at all</option>
                <option value="mobile">a mobile number</option>
                <option value="email">an email address</option>
                <option value="number">a number</option>
                <option value="date">a date (DD/MM/YYYY)</option>
                <option value="length">a certain length</option>
                <option value="regex">matching a pattern</option>
              </select>
            </Field>
            {validationType === 'regex' && (
              <Field label="Pattern" wide hint="A regular expression the whole answer must match, e.g. ^[A-Z]{2}\d{6}$.">
                <input value={(validation.pattern as string) ?? ''} onChange={(e) => setValidation({ pattern: e.target.value })} className={`${FIELD} font-mono`} spellCheck={false} />
              </Field>
            )}
            {validationType === 'length' && (
              <>
                <Field label="At least (characters)">
                  <input type="number" min={0} value={(validation.min as number) ?? ''} onChange={(e) => setValidation({ min: e.target.value === '' ? undefined : Number(e.target.value) })} className={FIELD} />
                </Field>
                <Field label="At most (characters)">
                  <input type="number" min={0} value={(validation.max as number) ?? ''} onChange={(e) => setValidation({ max: e.target.value === '' ? undefined : Number(e.target.value) })} className={FIELD} />
                </Field>
              </>
            )}
            <Field label="What to say when the answer is wrong" wide hint="It asks again after saying this. Leave blank for a sensible default.">
              <input value={(validation.error as string) ?? ''} onChange={(e) => setValidation({ error: e.target.value })} className={FIELD} placeholder="Please enter a valid 10-digit mobile number." />
            </Field>
            {target("Then go to", "next")}
          </>
        )}

        {type === 'condition' && (
          <>
            <Field label="Look at" hint="A value remembered earlier — from a question, a system, or Set values.">
              <input value={(node.var as string) ?? ''} onChange={(e) => set('var', e.target.value)} className={`${FIELD} font-mono`} placeholder="order_status" />
            </Field>
            {target("If nothing matches, go to", "default")}
          </>
        )}

        {(type === 'action' || type === 'dynamic_list') && (
          <>
            <Field
              label="Which system"
              hint={actionCall ?? (actions.length === 0 ? 'No actions yet — add one under Actions on the Bots page.' : 'The action decides the address, the method and the credential.')}
              mono={actionCall !== null}
            >
              <select value={(node.action as string) ?? ''} onChange={(e) => set('action', e.target.value)} className={FIELD}>
                <option value="">Choose…</option>
                {actions.map((a) => <option key={a.actionKey} value={a.actionKey}>{a.name || a.actionKey}</option>)}
              </select>
            </Field>
            {type === 'action' && (
              <Field label="Wait for the answer?">
                <select value={(node.mode as string) ?? 'pull'} onChange={(e) => set('mode', e.target.value)} className={FIELD}>
                  <option value="pull">Yes — the flow uses what comes back</option>
                  <option value="push">No — send it and carry on</option>
                </select>
              </Field>
            )}
            {chosenAction && actionMethod !== 'GET' && (
              <div className="@md:col-span-2">
                <BodyEditor value={node.body} onChange={(next) => set('body', next)} />
              </div>
            )}

            {chosenAction && onTest && (
              <div className="@md:col-span-2 rounded-lg border border-(--color-brand)/30 bg-(--color-brand-wash)/40 p-3">
                <p className="text-body font-medium">
                  {type === 'dynamic_list' ? 'Try the call, then click the list to offer' : 'Try the call, then click what to remember'}
                </p>
                <p className="mb-3 text-small text-(--color-muted)">
                  {type === 'dynamic_list'
                    ? 'The answer appears below. Click the list of items to use it as the menu, then click a field inside one item to remember it about the chosen one.'
                    : 'The answer appears below. Click a field and it is remembered as a variable the rest of the flow can use — no paths to type.'}
                </p>
                <TryAction
                  action={chosenAction}
                  body={node.body}
                  test={onTest}
                  showAddress={false}
                  picked={pickedPaths}
                  pickLists={type === 'dynamic_list'}
                  pickHint={type === 'dynamic_list' ? 'Click a list to offer it, or a field to remember it.' : 'Click a field to remember it.'}
                  onPick={(pick) => {
                    if (pick.kind === 'list') {
                      const first = (pick.value as unknown[])[0];
                      setSampleItem(first && typeof first === 'object' ? (first as Record<string, unknown>) : null);
                      set('items_path', pick.path);
                      return;
                    }
                    const listPath = (node.items_path as string) ?? '';
                    if (type === 'dynamic_list' && listPath && pick.path.startsWith(`${listPath}.`)) {
                      // A field inside an item: `items.0.Name` → field_map name ← Name.
                      const field = pick.path.slice(listPath.length + 1).split('.').slice(1).join('.');
                      if (field) {
                        const map = (node.field_map as Record<string, unknown>) ?? {};
                        set('field_map', { ...map, [variableNameFor(field, Object.keys(map))]: field });
                        return;
                      }
                    }
                    const map = (node.response_map as Record<string, unknown>) ?? {};
                    set('response_map', { ...map, [variableNameFor(pick.path, Object.keys(map))]: pick.path });
                  }}
                />
              </div>
            )}

            <div className="@md:col-span-2">
              <PairEditor
                label="Remembered from the answer"
                hint={onTest && chosenAction ? 'Filled by clicking above; edit or remove here.' : 'Each row stores part of the answer as a variable. The path is dotted: data.customer.id, items.0.name.'}
                keyLabel="Remember as"
                valueLabel="Where it is in the answer"
                pairs={(node.response_map as Record<string, unknown>) ?? {}}
                onChange={(next) => set('response_map', next)}
                placeholderKey="customer_id"
                placeholderValue="data.CustomerID"
              />
            </div>

            {type === 'dynamic_list' && (
              <>
                <Field label="The list of items is at" hint={sampleItem ? 'Picked from the answer above.' : 'Dotted path to the list — or pick it from the answer above.'}>
                  <input value={(node.items_path as string) ?? ''} onChange={(e) => set('items_path', e.target.value)} className={`${FIELD} font-mono`} placeholder="data.Products" />
                </Field>
                <Field label="Remember the choice as" hint="The chosen item's title; its id lands in {{name_id}}.">
                  <input value={(node.var as string) ?? ''} onChange={(e) => set('var', e.target.value)} className={`${FIELD} font-mono`} placeholder="product" />
                </Field>
                <Field label="Each item's id is the field" hint={sampleItem ? 'Choose from the item\'s fields, or type one.' : undefined}>
                  <input list={`${id}-item-fields`} value={(node.id_field as string) ?? ''} onChange={(e) => set('id_field', e.target.value || undefined)} className={`${FIELD} font-mono`} placeholder="id" />
                </Field>
                <Field label="Each item's title is the field">
                  <input list={`${id}-item-fields`} value={(node.title_field as string) ?? ''} onChange={(e) => set('title_field', e.target.value || undefined)} className={`${FIELD} font-mono`} placeholder="name" />
                </Field>
                {sampleItem && (
                  <datalist id={`${id}-item-fields`}>
                    {Object.keys(sampleItem).map((k) => <option key={k} value={k} />)}
                  </datalist>
                )}
                <Field label="Title, written out (optional)" hint="Overrides the title field. {{…}} here are the item's own fields.">
                  <input value={(node.title_template as string) ?? ''} onChange={(e) => set('title_template', e.target.value || undefined)} className={FIELD} placeholder="{{name}} · {{model}}" />
                </Field>
                <Field label="Second line (optional)" hint="A field name, or written out with {{…}}.">
                  <input
                    list={`${id}-item-fields`}
                    value={(node.description_template as string) ?? (node.description_field as string) ?? ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      // A bare field name and a template are two keys to the runner; one box here.
                      setMany(v.includes('{{') ? { description_template: v, description_field: undefined } : { description_field: v || undefined, description_template: undefined });
                    }}
                    className={FIELD}
                    placeholder="serial_no"
                  />
                </Field>
                {target("After they choose, go to", "next")}
                {target("If nothing came back, go to", "empty_next", "Carry on to the same step")}
                <Field label="What to say when nothing came back (optional)" wide>
                  <input value={(node.empty_text as string) ?? ''} onChange={(e) => set('empty_text', e.target.value || undefined)} className={FIELD} placeholder="We could not find any products on your account." />
                </Field>
                <div className="@md:col-span-2">
                  <PairEditor
                    label="Remembered from the chosen item"
                    hint={sampleItem ? 'Filled by clicking a field inside an item above; edit or remove here.' : 'Each row copies a field of the item they picked into a variable.'}
                    keyLabel="Remember as"
                    valueLabel="Field on the item"
                    pairs={(node.field_map as Record<string, unknown>) ?? {}}
                    onChange={(next) => set('field_map', Object.keys(next).length ? next : undefined)}
                    placeholderKey="product_id"
                    placeholderValue="ProductID"
                  />
                </div>
                <div className="@md:col-span-2">
                  <ExtraRowsEditor node={node} ids={ids} self={id} onChange={onChange} onCreateStep={onCreateStep} />
                </div>
              </>
            )}
            {type === 'action' && target("Then go to", "next")}
          </>
        )}

        {type === 'set' && (
          <>
            <div className="@md:col-span-2">
              <PairEditor
                label="Values to remember"
                hint="A value may contain {{another_variable}}."
                keyLabel="Variable"
                valueLabel="Value"
                pairs={(node.set as Record<string, unknown>) ?? {}}
                onChange={(next) => set('set', next)}
                placeholderKey="query_type"
                placeholderValue="Complaint"
              />
            </div>
            {target("Then go to", "next")}
          </>
        )}

        {type === 'message' && target("Then go to", "next")}

        {type === 'products' && (
          <>
            <Field label="What to show" wide>
              <div className="mt-1 flex flex-wrap gap-2">
                {([['catalogue', 'The whole catalogue'], ['products', 'Chosen products']] as const).map(([value, label]) => (
                  <label key={value} className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-body ${(node.mode ?? 'catalogue') === value ? 'border-(--color-brand) bg-(--color-brand-wash)' : 'border-(--color-line)'}`}>
                    <input type="radio" name={`mode-${id}`} checked={(node.mode ?? 'catalogue') === value} onChange={() => set('mode', value)} disabled={readOnly} />
                    {label}
                  </label>
                ))}
              </div>
            </Field>
            {node.mode === 'products' && (
              <div className="@md:col-span-2">
                <ProductsField
                  chosen={Array.isArray(node.products) ? (node.products as unknown[]).map(String) : []}
                  onChange={(next) => set('products', next)}
                  onProducts={onProducts}
                  readOnly={readOnly}
                />
              </div>
            )}
            {target("Then go to", "next")}
          </>
        )}

        {type === 'template' && (
          <>
            <Field
              label="Which template"
              hint="The name exactly as it appears in Templates, and it has to be approved."
            >
              <input
                list={`templates-${id}`}
                value={(node.template as string) ?? ''}
                onChange={(e) => set('template', e.target.value)}
                className={`${FIELD} mt-1`}
                placeholder="order_ready"
              />
              {(names?.templates?.length ?? 0) > 0 && (
                <datalist id={`templates-${id}`}>
                  {names!.templates!.map((t) => <option key={t} value={t} />)}
                </datalist>
              )}
            </Field>
            <Field label="Header value (only if the template's header has one)">
              <input
                value={(node.header_text as string) ?? ''}
                onChange={(e) => set('header_text', e.target.value)}
                className={`${FIELD} mt-1`}
                placeholder="{{customer_name}}"
              />
            </Field>
            <div className="@md:col-span-2">
              <ListEditor
                label="Values, in the template's order"
                hint="One per {{1}}, {{2}}… in the template's text. A value may be a variable."
                items={Array.isArray(node.variables) ? (node.variables as unknown[]).map(String) : []}
                onChange={(next) => set('variables', next)}
                placeholder="{{customer_name}}"
                addLabel="Add a value"
                readOnly={readOnly}
              />
            </div>
            <p className="text-small text-(--color-muted) @md:col-span-2">
              A template is the only message that still reaches someone more than 24 hours after they
              last wrote to you — and it is charged. Everything else here is free inside that window.
            </p>
            {target("Then go to", "next")}
          </>
        )}

        {type === 'tag' && (
          <>
            <div className="@md:col-span-2 grid gap-4 @md:grid-cols-2">
              <ListEditor
                label="Labels to add"
                items={Array.isArray(node.add) ? (node.add as unknown[]).map(String) : []}
                onChange={(next) => set('add', next)}
                placeholder="quote sent"
                addLabel="Add a label"
                suggestions={names?.tags}
                suggestId={`tags-add-${id}`}
                readOnly={readOnly}
              />
              <ListEditor
                label="Labels to take off"
                items={Array.isArray(node.remove) ? (node.remove as unknown[]).map(String) : []}
                onChange={(next) => set('remove', next)}
                placeholder="awaiting quote"
                addLabel="Add a label"
                suggestions={names?.tags}
                suggestId={`tags-remove-${id}`}
                readOnly={readOnly}
              />
            </div>
            <p className="text-small text-(--color-muted) @md:col-span-2">
              The label has to exist already — add it in Settings first. Nothing is said to the customer.
            </p>
            {target("Then go to", "next")}
          </>
        )}

        {type === 'wait' && (
          <>
            <Field label="Wait for" hint="Then the bot carries on by itself.">
              <div className="mt-1 flex items-center gap-2">
                <input
                  value={String(node.minutes ?? '')}
                  onChange={(e) => setMany({ minutes: Number(e.target.value.replace(/\D/g, '').slice(0, 4)) || '', seconds: undefined })}
                  inputMode="numeric"
                  className={`${FIELD} w-24 text-center`}
                  placeholder="10"
                />
                <span className="text-body text-(--color-muted)">minutes</span>
              </div>
            </Field>
            <p className="text-small text-(--color-muted) @md:col-span-2">
              If the customer writes before the time is up, the bot carries on straight away rather than
              making them wait. Keep it well under 24 hours: after that only a template can reach them.
            </p>
            {target("Then go to", "next")}
          </>
        )}

        {type === 'jump' && (
          <Field
            label="Which bot"
            wide
            hint="It has to be live on this number. Everything collected so far goes with the customer."
          >
            <input
              list={`flows-${id}`}
              value={(node.flow as string) ?? ''}
              onChange={(e) => set('flow', e.target.value)}
              className={`${FIELD} mt-1`}
              placeholder="Order tracking"
            />
            {(names?.flows?.length ?? 0) > 0 && (
              <datalist id={`flows-${id}`}>
                {names!.flows!.map((f) => <option key={f} value={f} />)}
              </datalist>
            )}
          </Field>
        )}
      </div>

      {(type === 'list' || type === 'buttons') && (
        <OptionsEditor type={type} node={node} ids={ids} self={id} onChange={onChange} onCreateStep={onCreateStep} />
      )}

      {type === 'condition' && (
        <CasesEditor node={node} ids={ids} self={id} onChange={onChange} onCreateStep={onCreateStep} />
      )}

      {/* --------------------------------------------------- the rest of it -- */}
      <MoreFields type={type} node={node} set={set} />
    </fieldset>
  );
}

/**
 * The step's name, committed when you leave the box.
 *
 * Renaming rewrites every reference to the step, and doing that on every keystroke
 * meant that typing "welcome" over "start" went through "w", "we", "wel"… each of
 * which briefly existed as a step every transition pointed at. Committed once, and
 * refused when the name is taken, which the per-keystroke version could not do
 * because "welcom" is not taken until "welcome" is.
 */
function NameField({ id, taken, onRename }: { id: string; taken: string[]; onRename: (next: string) => void }) {
  const [name, setName] = useState(id);
  useEffect(() => setName(id), [id]);

  const trimmed = name.trim().replace(/\s+/g, '_');
  const clash = trimmed !== id && taken.includes(trimmed);
  const commit = () => {
    if (!trimmed || clash || trimmed === id) { setName(id); return; }
    onRename(trimmed);
  };

  return (
    <label className="block min-w-0 flex-1">
      <span className="sr-only">Step name</span>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur(); } if (e.key === 'Escape') setName(id); }}
        aria-invalid={clash || undefined}
        className={`${FIELD} mt-0 font-medium ${clash ? 'border-(--color-bad)' : ''}`}
      />
      {clash && <span className="text-small text-(--color-bad)">There is already a step called {trimmed}.</span>}
    </label>
  );
}

/** Optional fields the runner reads, folded away so the common case stays short. */
function MoreFields({ type, node, set }: { type: FlowNodeType; node: FlowNode; set: (key: string, value: unknown) => void }) {
  const menu = type === 'list' || type === 'buttons' || type === 'dynamic_list';
  const shop = type === 'products';
  const call = type === 'action' || type === 'dynamic_list';
  const alsoSet = type !== 'set';
  if (!menu && !shop && !call && !alsoSet) return null;

  const has = (key: string) => node[key] !== undefined && node[key] !== null && node[key] !== '';
  const filled = ['header', 'footer', 'section_title', 'invalid_msg', 'timeout', 'status_var', 'headers', 'set'].filter(has).length;

  return (
    <details className="group mt-4 rounded-lg border border-(--color-line-dim)">
      <summary className="cursor-pointer list-none px-3 py-2 text-small font-medium text-(--color-muted) select-none">
        <span className="mr-1.5 inline-block transition group-open:rotate-90">▸</span>
        More{filled ? ` · ${filled} set` : ''}
      </summary>
      <div className="grid gap-4 border-t border-(--color-line-dim) p-3 @md:grid-cols-2">
        {menu && (
          <>
            {type !== 'buttons' && (
              <Field label="Button that opens the menu" count={[(node.button as string) ?? '', LIMITS.button]}>
                <input value={(node.button as string) ?? ''} onChange={(e) => set('button', e.target.value || undefined)} className={FIELD} placeholder="Menu" />
              </Field>
            )}
            {type !== 'buttons' && (
              <Field label="Section title" count={[(node.section_title as string) ?? '', LIMITS.section]}>
                <input value={(node.section_title as string) ?? ''} onChange={(e) => set('section_title', e.target.value || undefined)} className={FIELD} placeholder="Options" />
              </Field>
            )}
            <Field label="Header (optional)" count={[(node.header as string) ?? '', LIMITS.header]}>
              <input value={(node.header as string) ?? ''} onChange={(e) => set('header', e.target.value || undefined)} className={FIELD} />
            </Field>
            <Field label="Footer (optional)" count={[(node.footer as string) ?? '', LIMITS.footer]}>
              <input value={(node.footer as string) ?? ''} onChange={(e) => set('footer', e.target.value || undefined)} className={FIELD} />
            </Field>
            <Field label="If they type instead of choosing" wide hint="Said before the menu is shown again.">
              <input value={(node.invalid_msg as string) ?? ''} onChange={(e) => set('invalid_msg', e.target.value || undefined)} className={FIELD} placeholder="Please choose one of the options below. 🙂" />
            </Field>
          </>
        )}
        {shop && (
          <>
            <Field label="Header (optional)" hint="Shown above a list of products; a single product and the catalogue take none." count={[(node.header as string) ?? '', LIMITS.header]}>
              <input value={(node.header as string) ?? ''} onChange={(e) => set('header', e.target.value || undefined)} className={FIELD} placeholder="Products" />
            </Field>
            <Field label="Footer (optional)" count={[(node.footer as string) ?? '', LIMITS.footer]}>
              <input value={(node.footer as string) ?? ''} onChange={(e) => set('footer', e.target.value || undefined)} className={FIELD} />
            </Field>
          </>
        )}
        {call && (
          <>
            <Field label="Give up after (seconds)" hint="8 if blank.">
              <input type="number" min={1} max={60} value={(node.timeout as number) ?? ''} onChange={(e) => set('timeout', e.target.value === '' ? undefined : Number(e.target.value))} className={FIELD} />
            </Field>
            <Field label="Remember the HTTP status as" hint="Branch on it to handle a system being down.">
              <input value={(node.status_var as string) ?? ''} onChange={(e) => set('status_var', e.target.value || undefined)} className={`${FIELD} font-mono`} placeholder="lookup_status" />
            </Field>
            <div className="@md:col-span-2">
              <PairEditor
                label="Extra request headers"
                hint="Credentials do not go here — the action carries those. Values may contain {{a_variable}}."
                keyLabel="Header"
                valueLabel="Value"
                pairs={(node.headers as Record<string, unknown>) ?? {}}
                onChange={(next) => set('headers', Object.keys(next).length ? next : undefined)}
                placeholderKey="X-Source"
                placeholderValue="whatsapp"
              />
            </div>
          </>
        )}
        {alsoSet && (
          <div className="@md:col-span-2">
            <PairEditor
              label="Also remember"
              hint="Values set when this step runs, before it says anything."
              keyLabel="Variable"
              valueLabel="Value"
              pairs={(node.set as Record<string, unknown>) ?? {}}
              onChange={(next) => set('set', Object.keys(next).length ? next : undefined)}
              placeholderKey="query_type"
              placeholderValue="Complaint"
            />
          </div>
        )}
      </div>
    </details>
  );
}

/** Enough rows to show what is in the box. A message clipped mid-sentence in the
 *  editor is the one thing a builder must not do — that text is the product. */
function rows(value: unknown) {
  const text = typeof value === 'string' ? value : '';
  return Math.min(14, Math.max(3, text.split('\n').length + Math.floor(text.length / 90)));
}

function describeAction(action: BotAction): string {
  try {
    const config = JSON.parse(action.config) as { method?: string; url?: string };
    return `${config.method ?? 'POST'} ${config.url ?? ''}`.trim();
  } catch {
    return action.actionKey;
  }
}

// ------------------------------------------------------------------- pieces --

function Field({ label, hint, wide, count, mono, children }: {
  label: string; hint?: string; wide?: boolean; count?: [string, number]; mono?: boolean; children: ReactNode;
}) {
  const over = count ? count[0].length > count[1] : false;
  return (
    <label className={`block min-w-0 ${wide ? '@md:col-span-2' : ''}`}>
      <span className="flex items-baseline gap-2 text-small font-medium text-(--color-muted)">
        {label}
        {count && count[0].length > 0 && (
          <span className={`ml-auto font-normal tabular-nums ${over ? 'text-(--color-bad)' : 'text-(--color-faint)'}`}>
            {count[0].length}/{count[1]}
          </span>
        )}
      </span>
      {children}
      {hint && <span className={`mt-1 block text-small text-(--color-faint) ${mono ? 'font-mono break-all' : ''}`}>{hint}</span>}
    </label>
  );
}

/**
 * A "goes to" dropdown.
 *
 * `create` and `onCreate` travel together: the first is the editor's "make a step and
 * apply this patch", the second says what the patch is for this particular field.
 */
function TargetSelect({ value, ids, self, none, onChange, create, onCreate, className }: {
  value: string;
  ids: string[];
  self: string;
  none: string;
  onChange: (next: string | null) => void;
  create?: (link: (newId: string) => FlowNode) => void;
  onCreate?: (newId: string) => FlowNode;
  className?: string;
}) {
  const others = ids.filter((n) => n !== self);
  const dangling = value && !ids.includes(value);
  return (
    <select
      value={value}
      onChange={(e) => {
        if (e.target.value === NEW_STEP) { if (create && onCreate) create(onCreate); return; }
        onChange(e.target.value || null);
      }}
      className={`${className ?? FIELD} ${dangling ? 'border-(--color-bad)' : ''}`}
    >
      <option value="">{none}</option>
      {dangling && <option value={value}>{value} (does not exist)</option>}
      {others.map((n) => <option key={n} value={n}>{n}</option>)}
      {create && onCreate && <option value={NEW_STEP}>＋ A new step…</option>}
    </select>
  );
}

/**
 * The options on a menu, and where each one leads.
 *
 * One row is one option: adding one writes both the row and its transition, removing
 * one removes both, renaming one carries its destination with it. The two structures
 * have to agree on every id, and asking a person to keep them agreeing by hand is how
 * dangling targets get made.
 */
function OptionsEditor({ type, node, ids, self, onChange, onCreateStep }: {
  type: 'list' | 'buttons';
  node: FlowNode;
  ids: string[];
  self: string;
  onChange: (patch: FlowNode) => void;
  onCreateStep?: (link: (newId: string) => FlowNode) => void;
}) {
  type Option = { id: string; title: string; description?: string };
  const field = type === 'list' ? 'rows' : 'buttons';
  const rowsIn: Option[] = (node[field] as Option[] | undefined) ?? [];
  const transitions = (node.transitions as Record<string, string>) ?? {};
  const max = type === 'list' ? 10 : 3;
  const titleMax = type === 'list' ? LIMITS.rowTitle : LIMITS.buttonTitle;

  const patched = (next: Option[], nextTransitions: Record<string, string>): FlowNode =>
    ({ ...node, [field]: next, transitions: nextTransitions });
  const write = (next: Option[], nextTransitions: Record<string, string>) => onChange(patched(next, nextTransitions));

  const patch = (i: number, key: keyof Option, value: string) => {
    const next = rowsIn.map((r, n) => (n === i ? { ...r, [key]: value } : r));
    if (key !== 'id') return write(next, transitions);
    const { [rowsIn[i]?.id ?? '']: target, ...rest } = transitions;
    write(next, target === undefined ? rest : { ...rest, [value]: target });
  };

  const remove = (i: number) => {
    const { [rowsIn[i]?.id ?? '']: _gone, ...rest } = transitions;
    write(rowsIn.filter((_, n) => n !== i), rest);
  };

  const move = (i: number, by: -1 | 1) => {
    const next = [...rowsIn];
    const [row] = next.splice(i, 1);
    next.splice(i + by, 0, row!);
    write(next, transitions);
  };

  const add = () => {
    let id = 'option';
    let n = 1;
    while (rowsIn.some((r) => r.id === id)) id = `option_${++n}`;
    write([...rowsIn, { id, title: '' }], transitions);
  };

  const setTarget = (rowId: string, target: string | null) => {
    if (target) return write(rowsIn, { ...transitions, [rowId]: target });
    const { [rowId]: _g, ...rest } = transitions;
    write(rowsIn, rest);
  };

  return (
    <div className="mt-4">
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="text-small font-medium text-(--color-muted)">{type === 'list' ? 'Menu options' : 'Buttons'}</span>
        <span className="text-small text-(--color-faint)">
          {rowsIn.length}/{max} · WhatsApp cuts titles at {titleMax} characters.
        </span>
      </div>

      {rowsIn.length === 0 && (
        <p className="mt-2 rounded-lg border border-dashed border-(--color-line) px-3 py-3 text-small text-(--color-muted)">
          None yet — a menu with no options shows the customer nothing.
        </p>
      )}

      <div className="mt-2 flex flex-col gap-2">
        {rowsIn.map((row, i) => (
          <div key={i} className="grid gap-2 rounded-lg border border-(--color-line-dim) bg-(--color-paper)/40 p-3 @md:grid-cols-2 @2xl:grid-cols-[1fr_1fr_1fr_auto]">
            <label className="block min-w-0">
              <span className="text-small text-(--color-muted)">What the customer sees</span>
              <input value={row.title ?? ''} onChange={(e) => patch(i, 'title', e.target.value)} className={`${SMALL_FIELD} mt-1 ${(row.title?.length ?? 0) > titleMax ? 'border-(--color-bad)' : ''}`} placeholder={type === 'list' ? 'Track my order' : 'Yes'} />
            </label>
            <label className="block min-w-0">
              <span className="text-small text-(--color-muted)">Value sent back</span>
              <input value={row.id ?? ''} onChange={(e) => patch(i, 'id', e.target.value)} className={`${SMALL_FIELD} mt-1 font-mono`} />
            </label>
            <label className="block min-w-0">
              <span className="text-small text-(--color-muted)">Goes to</span>
              <TargetSelect
                value={transitions[row.id] ?? ''}
                ids={ids}
                self={self}
                none="Nothing happens"
                className={`${SMALL_FIELD} mt-1`}
                onChange={(next) => setTarget(row.id, next)}
                create={onCreateStep}
                onCreate={onCreateStep ? (newId) => patched(rowsIn, { ...transitions, [row.id]: newId }) : undefined}
              />
            </label>
            <div className="flex items-end gap-1">
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className={GHOST} aria-label="Move up">↑</button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === rowsIn.length - 1} className={GHOST} aria-label="Move down">↓</button>
              <button type="button" onClick={() => remove(i)} className={`${GHOST} hover:text-(--color-bad)`} aria-label="Remove option">✕</button>
            </div>
            {type === 'list' && (
              <label className="block @md:col-span-2 @2xl:col-span-4">
                <span className="text-small text-(--color-muted)">Second line (optional)</span>
                <input value={row.description ?? ''} onChange={(e) => patch(i, 'description', e.target.value)} className={`${SMALL_FIELD} mt-1`} maxLength={LIMITS.rowDescription} />
              </label>
            )}
          </div>
        ))}
      </div>

      <button type="button" onClick={add} disabled={rowsIn.length >= max} className="mt-2 rounded-lg border border-(--color-line) px-3.5 py-2 text-body disabled:opacity-40">
        {rowsIn.length >= max ? `WhatsApp allows ${max}` : type === 'list' ? 'Add an option' : 'Add a button'}
      </button>
    </div>
  );
}

/** The branches of a condition: a value, and the step it leads to. */
function CasesEditor({ node, ids, self, onChange, onCreateStep }: {
  node: FlowNode;
  ids: string[];
  self: string;
  onChange: (patch: FlowNode) => void;
  onCreateStep?: (link: (newId: string) => FlowNode) => void;
}) {
  const cases = (node.cases as Record<string, string>) ?? {};
  const entries = Object.entries(cases);

  const patched = (next: [string, string][]): FlowNode => ({ ...node, cases: Object.fromEntries(next) });
  const write = (next: [string, string][]) => onChange(patched(next));

  return (
    <div className="mt-4">
      <span className="text-small font-medium text-(--color-muted)">
        When <span className="font-mono">{(node.var as string) || 'the value'}</span> is…
      </span>
      <p className="text-small text-(--color-faint)">
        Compared as text, so a number is "3" and a yes/no is "true" or "false". Anything else takes the "nothing matches" route.
      </p>

      <div className="mt-2 flex flex-col gap-2">
        {entries.map(([value, target], i) => (
          <div key={i} className="grid gap-2 @md:grid-cols-[1fr_1fr_auto]">
            <input
              value={value}
              onChange={(e) => write(entries.map((p, n) => (n === i ? [e.target.value, p[1]] : p)))}
              placeholder="true"
              aria-label="Value"
              className={`${SMALL_FIELD} font-mono`}
            />
            <TargetSelect
              value={target}
              ids={ids}
              self={self}
              none="Nothing happens"
              className={SMALL_FIELD}
              onChange={(next) => write(entries.map((p, n) => (n === i ? [p[0], next ?? ''] : p)))}
              create={onCreateStep}
              onCreate={onCreateStep ? (newId) => patched(entries.map((p, n) => (n === i ? [p[0], newId] : p))) : undefined}
            />
            <button type="button" onClick={() => write(entries.filter((_, n) => n !== i))} className={GHOST} aria-label="Remove case">✕</button>
          </div>
        ))}
      </div>

      <button type="button" onClick={() => write([...entries, ['', '']])} className="mt-2 rounded-lg border border-(--color-line) px-3.5 py-2 text-body">
        Add a case
      </button>
    </div>
  );
}

/** Fixed rows after a system's items — "none of these", "talk to someone". */
function ExtraRowsEditor({ node, ids, self, onChange, onCreateStep }: {
  node: FlowNode;
  ids: string[];
  self: string;
  onChange: (patch: FlowNode) => void;
  onCreateStep?: (link: (newId: string) => FlowNode) => void;
}) {
  type Extra = { id: string; title: string; target?: string | null };
  const extras: Extra[] = Array.isArray(node.extra_rows) ? (node.extra_rows as Extra[]) : [];
  const patched = (next: Extra[]): FlowNode => ({ ...node, extra_rows: next.length ? next : undefined });
  const write = (next: Extra[]) => onChange(patched(next));
  const patch = (i: number, key: keyof Extra, value: string | null) => write(extras.map((r, n) => (n === i ? { ...r, [key]: value } : r)));

  return (
    <div>
      <span className="text-small font-medium text-(--color-muted)">Fixed options after the items</span>
      <p className="text-small text-(--color-faint)">An escape hatch — "My product isn't listed". Up to 9, leaving room for the items.</p>
      <div className="mt-2 flex flex-col gap-2">
        {extras.map((row, i) => (
          <div key={i} className="grid gap-2 @md:grid-cols-2 @2xl:grid-cols-[1fr_1fr_1fr_auto]">
            <input value={row.title ?? ''} onChange={(e) => patch(i, 'title', e.target.value)} placeholder="Something else" aria-label="What the customer sees" className={SMALL_FIELD} />
            <input value={row.id ?? ''} onChange={(e) => patch(i, 'id', e.target.value)} placeholder="other" aria-label="Value sent back" className={`${SMALL_FIELD} font-mono`} />
            <TargetSelect
              value={row.target ?? ''}
              ids={ids}
              self={self}
              none="Carry on as if nothing was chosen"
              className={SMALL_FIELD}
              onChange={(next) => patch(i, 'target', next)}
              create={onCreateStep}
              onCreate={onCreateStep ? (newId) => patched(extras.map((r, n) => (n === i ? { ...r, target: newId } : r))) : undefined}
            />
            <button type="button" onClick={() => write(extras.filter((_, n) => n !== i))} className={GHOST} aria-label="Remove option">✕</button>
          </div>
        ))}
      </div>
      <button type="button" onClick={() => write([...extras, { id: extras.length ? `other_${extras.length + 1}` : 'other', title: '' }])} disabled={extras.length >= 9} className="mt-2 rounded-lg border border-(--color-line) px-3.5 py-2 text-body disabled:opacity-40">
        Add a fixed option
      </button>
    </div>
  );
}

/**
 * A plain list of strings, as rows.
 *
 * Used by the steps that carry a handful of names or values — a template's parameters,
 * the labels to add. `suggestions` feeds a datalist rather than a select: the value is
 * a name the client also sees on another screen, and a name that does not exist is
 * caught by the validator, so being able to type one that is not in the list yet is a
 * feature, not a hole.
 */
function ListEditor({ label, hint, items, onChange, placeholder, addLabel, suggestions, suggestId, readOnly }: {
  label: string;
  hint?: string;
  items: string[];
  onChange: (next: string[]) => void;
  placeholder: string;
  addLabel: string;
  suggestions?: string[];
  suggestId?: string;
  readOnly?: boolean;
}) {
  const write = (next: string[]) => onChange(next);

  return (
    <div>
      <span className="text-small font-medium text-(--color-muted)">{label}</span>
      {hint && <p className="mt-0.5 text-small text-(--color-muted)">{hint}</p>}

      <div className="mt-2 flex flex-col gap-2">
        {items.map((item, i) => (
          <div key={i} className="flex items-center gap-2">
            <input
              list={suggestId}
              value={item}
              onChange={(e) => write(items.map((v, j) => (j === i ? e.target.value : v)))}
              className={SMALL_FIELD}
              placeholder={placeholder}
            />
            <button
              type="button"
              onClick={() => write(items.filter((_, j) => j !== i))}
              className={`${GHOST} shrink-0 hover:text-(--color-bad)`}
              aria-label={`Remove ${item || placeholder}`}
            >
              Remove
            </button>
          </div>
        ))}
      </div>

      {(suggestions?.length ?? 0) > 0 && suggestId && (
        <datalist id={suggestId}>
          {suggestions!.map((v) => <option key={v} value={v} />)}
        </datalist>
      )}

      <button
        type="button"
        onClick={() => write([...items, ''])}
        disabled={readOnly || items.length >= 12}
        className="mt-2 rounded-lg border border-(--color-line) px-3.5 py-2 text-body disabled:opacity-40"
      >
        {addLabel}
      </button>
    </div>
  );
}

/** A map of name to value, as rows. Used for set, response maps and headers. */
function PairEditor({ label, hint, keyLabel, valueLabel, pairs, onChange, placeholderKey, placeholderValue }: {
  label: string;
  hint: string;
  keyLabel: string;
  valueLabel: string;
  pairs: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  placeholderKey: string;
  placeholderValue: string;
}) {
  // Rows are kept locally so a half-typed key ("customer_i") is not dropped by the
  // write below, which cannot store a row with no key.
  const [draft, setDraft] = useState<[string, string][] | null>(null);
  const entries: [string, string][] = draft ?? Object.entries(pairs).map(([k, v]) => [k, String(v ?? '')]);

  const write = (next: [string, string][]) => {
    setDraft(next.some(([k]) => k === '') ? next : null);
    onChange(Object.fromEntries(next.filter(([k]) => k !== '')));
  };

  return (
    <div>
      {label && <span className="text-small font-medium text-(--color-muted)">{label}</span>}
      <p className="text-small text-(--color-faint)">{hint}</p>

      <div className="mt-2 flex flex-col gap-2">
        {entries.map(([key, value], i) => (
          <div key={i} className="grid gap-2 @md:grid-cols-[1fr_1fr_auto]">
            <input value={key} onChange={(e) => write(entries.map((p, n) => (n === i ? [e.target.value, p[1]] : p)))} placeholder={placeholderKey} aria-label={keyLabel} className={`${SMALL_FIELD} font-mono`} />
            <input value={value} onChange={(e) => write(entries.map((p, n) => (n === i ? [p[0], e.target.value] : p)))} placeholder={placeholderValue} aria-label={valueLabel} className={`${SMALL_FIELD} font-mono`} />
            <button type="button" onClick={() => write(entries.filter((_, n) => n !== i))} className={GHOST} aria-label="Remove row">✕</button>
          </div>
        ))}
      </div>

      <button type="button" onClick={() => write([...entries, ['', '']])} className="mt-2 rounded-lg border border-(--color-line) px-3.5 py-2 text-body">
        Add one
      </button>
    </div>
  );
}

/**
 * What a call sends.
 *
 * Nearly every request body is a flat object — a mobile number, a customer id, a
 * complaint text — so the default is one row per field, values allowed to carry
 * `{{a_variable}}`. Anything nested is edited as JSON, and the switch is one click
 * either way. A body with a nested value cannot be shown as rows, so that switch is
 * refused rather than flattened.
 */
function BodyEditor({ value, onChange }: { value: unknown; onChange: (next: unknown) => void }) {
  const flat = value === undefined || value === null
    || (typeof value === 'object' && !Array.isArray(value)
      && Object.values(value as Record<string, unknown>).every((v) => v === null || ['string', 'number', 'boolean'].includes(typeof v)));
  const [json, setJson] = useState(!flat);
  const pairs = flat && value ? Object.fromEntries(Object.entries(value as Record<string, unknown>).map(([k, v]) => [k, String(v ?? '')])) : {};

  return (
    <div>
      <div className="flex items-baseline gap-2">
        <span className="text-small font-medium text-(--color-muted)">What to send</span>
        {(flat || json) && (
          <button type="button" onClick={() => setJson((v) => !v)} className="ml-auto text-small text-(--color-brand) hover:underline">
            {json ? (flat ? 'Edit as fields' : '') : 'Edit as JSON'}
          </button>
        )}
      </div>
      {json || !flat ? (
        <JsonField
          label=""
          hint="The request body, as JSON. Any value may contain {{a_variable}}."
          value={value}
          onChange={onChange}
          placeholder={'{\n  "mobile": "{{mobile}}"\n}'}
        />
      ) : (
        <PairEditor
          label=""
          hint="One row per field. A value may be {{a_variable}} the flow has collected. Leave empty to send nothing."
          keyLabel="Field"
          valueLabel="Value"
          pairs={pairs}
          onChange={(next) => onChange(Object.keys(next).length ? next : undefined)}
          placeholderKey="mobile"
          placeholderValue="{{mobile}}"
        />
      )}
    </div>
  );
}

/**
 * A JSON value edited as text.
 *
 * The text is local; the value is written through only when it parses, so a
 * half-typed object never reaches the definition as a string the runner would send
 * verbatim. Invalid text shows as such and stays on screen until it is fixed.
 */
function JsonField({ label, hint, value, onChange, placeholder }: {
  label: string; hint: string; value: unknown; onChange: (next: unknown) => void; placeholder?: string;
}) {
  const printed = value === undefined || value === null ? '' : typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  const [text, setText] = useState(printed);
  const [bad, setBad] = useState(false);

  // A change from outside (undo, the Definition tab) replaces what is here. A change
  // that came from this box does not: re-printing what was just typed would reformat
  // it under the cursor on every keystroke.
  const emitted = useRef(printed);
  useEffect(() => {
    if (printed === emitted.current) return;
    emitted.current = printed;
    setText(printed);
    setBad(false);
  }, [printed]);

  const emit = (next: unknown) => {
    emitted.current = next === undefined ? '' : JSON.stringify(next, null, 2);
    onChange(next);
  };

  return (
    <label className="block">
      <span className="flex items-baseline gap-2 text-small font-medium text-(--color-muted)">
        {label}
        {bad && <span className="ml-auto font-normal text-(--color-bad)">not valid JSON — not saved yet</span>}
      </span>
      <textarea
        value={text}
        onChange={(e) => {
          const next = e.target.value;
          setText(next);
          if (next.trim() === '') { setBad(false); emit(undefined); return; }
          try { emit(JSON.parse(next)); setBad(false); } catch { setBad(true); }
        }}
        rows={Math.min(12, Math.max(3, text.split('\n').length))}
        spellCheck={false}
        placeholder={placeholder}
        className={`${FIELD} font-mono text-small ${bad ? 'border-(--color-bad)' : ''}`}
      />
      <span className="mt-1 block text-small text-(--color-faint)">{hint}</span>
    </label>
  );
}

/**
 * Which products a "show products" step offers. With a catalogue to read, a
 * searchable list with checkboxes (up to thirty — WhatsApp's limit); without one
 * (the admin portal edits a client's flow without their catalogue), the ids as a
 * list to type. The ids are the retailer ids — the "content id" in Commerce Manager.
 */
function ProductsField({ chosen, onChange, onProducts, readOnly }: {
  chosen: string[]; onChange: (next: string[]) => void; onProducts?: () => Promise<CatalogProduct[]>; readOnly: boolean;
}) {
  const [products, setProducts] = useState<CatalogProduct[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [q, setQ] = useState('');

  useEffect(() => {
    if (!onProducts) return;
    let alive = true;
    onProducts().then((list) => { if (alive) setProducts(list); }).catch(() => { if (alive) setProblem('Could not load the catalogue.'); });
    return () => { alive = false; };
  }, [onProducts]);

  const toggle = (id: string, on: boolean) => {
    if (readOnly) return;
    if (on && chosen.length >= 30) return;
    onChange(on ? [...chosen, id] : chosen.filter((c) => c !== id));
  };

  if (!onProducts) {
    return (
      <Field label="Product ids, one per line" hint="The content id from Commerce Manager. Up to thirty." mono>
        <textarea
          value={chosen.join('\n')}
          onChange={(e) => onChange(e.target.value.split('\n').map((v) => v.trim()).filter(Boolean).slice(0, 30))}
          rows={Math.max(3, Math.min(8, chosen.length + 1))}
          className={`${FIELD} font-mono`}
          disabled={readOnly}
        />
      </Field>
    );
  }

  const term = q.trim().toLowerCase();
  const shown = (products ?? []).filter((p) => !term || p.name.toLowerCase().includes(term) || p.retailerId.toLowerCase().includes(term));

  return (
    <Field label={`Products to show · ${chosen.length} of 30`} hint="In the order ticked. The customer sees name, price and picture from the catalogue.">
      {problem && <p className="mt-1 text-small text-(--color-bad)">{problem}</p>}
      {products === null && !problem && <p className="mt-1 text-small text-(--color-muted)">Loading the catalogue…</p>}
      {products !== null && products.length === 0 && (
        <p className="mt-1 text-small text-(--color-muted)">The catalogue is empty or not connected. Products are added in Commerce Manager.</p>
      )}
      {products !== null && products.length > 0 && (
        <div className="mt-1 rounded-lg border border-(--color-line)">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products" aria-label="Search products" className="w-full border-b border-(--color-line) bg-transparent px-3 py-2 text-body outline-none" />
          <ul className="max-h-64 overflow-y-auto">
            {shown.map((p) => {
              const on = chosen.includes(p.retailerId);
              return (
                <li key={p.retailerId}>
                  <label className="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-(--color-sunken)">
                    <input type="checkbox" checked={on} disabled={readOnly || (!on && chosen.length >= 30)} onChange={(e) => toggle(p.retailerId, e.target.checked)} />
                    {p.imageUrl && <img src={p.imageUrl} alt="" className="size-9 shrink-0 rounded object-cover" />}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-body">{p.name}</span>
                      <span className="block truncate text-small text-(--color-muted)">{p.price ?? ''}{p.reviewStatus && p.reviewStatus !== 'approved' ? ` · ${p.reviewStatus}` : ''}</span>
                    </span>
                  </label>
                </li>
              );
            })}
            {shown.length === 0 && <li className="px-3 py-3 text-small text-(--color-muted)">Nothing matches.</li>}
          </ul>
        </div>
      )}
    </Field>
  );
}
